/* ==========================================================================
   <fe-eco-logos> — the partner-logo groups in the ecosystem section.

   Each group's tiles are a scroll-snapped strip, which the stylesheet makes
   and which works on its own: the tiles can be swiped, dragged or flicked
   without a line of this running. What this adds is the bar underneath — a
   step either way and a dot per place the strip can come to rest — and the
   answer to whether that bar is worth showing at all.

   The section prints a dot per tile, which is the most that could ever be
   wanted; how many are actually shown is decided here. A strip holding four
   tiles with three of them in view has two places it can stop, not four — the
   last two tiles can be reached, but never as the tile at the start — so the
   dots past the end of the scroll are dropped rather than left as positions
   that cannot be reached. A group whose logos all fit has nowhere to go, and
   its bar is not shown.

   The bar is printed hidden and unhidden here, so no visitor is left with a
   control that cannot act: without this script a group is simply a strip.
   ========================================================================== */

if (!customElements.get('fe-eco-logos')) {
  customElements.define(
    'fe-eco-logos',
    class FeEcoLogos extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        this.groups = [...this.querySelectorAll('[data-logo-group]')].map((root) => ({
          root,
          track: root.querySelector('[data-logos]'),
          nav: root.querySelector('[data-nav]'),
          dots: root.querySelector('[data-dots]'),
          prev: root.querySelector('[data-prev]'),
          next: root.querySelector('[data-next]'),
          tiles: [...root.querySelectorAll('[data-logos] > *')],
          index: 0,
          pages: 0,
        }));

        for (const group of this.groups) {
          if (!group.track || !group.nav) continue;

          group.nav.querySelectorAll('[data-goto]').forEach((dot) => {
            dot.addEventListener('click', () => this.slideTo(group, Number(dot.dataset.goto)));
          });

          /* Stepped from where the strip is, not from a tally of the clicks
             it has had: a swipe moves it too, and `index` is what both the
             scroll handler and the step below keep current. */
          group.prev?.addEventListener('click', () => this.slideTo(group, group.index - 1));
          group.next?.addEventListener('click', () => this.slideTo(group, group.index + 1));

          group.track.addEventListener('scroll', () => this.queueSync(group), { passive: true });
        }

        this.onResize = this.onResize.bind(this);
        addEventListener('resize', this.onResize, { passive: true });
        this.syncAll();
      }

      disconnectedCallback() {
        removeEventListener('resize', this.onResize);
        clearTimeout(this.resizeTimer);
      }

      /* A width change moves the breakpoint the tile count is read from, so
         every group is asked again rather than only the one that scrolled. */
      onResize() {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.syncAll(), 150);
      }

      syncAll() {
        for (const group of this.groups) this.sync(group);
      }

      slideTo(group, index) {
        const clamped = Math.max(0, Math.min(index, group.pages - 1));
        const tile = group.tiles[clamped];
        if (!tile || !group.track.clientWidth) return;

        /* Taken as read rather than waited for. The scroll handler confirms it
           a frame later, but two steps taken inside one frame — a double click
           on an arrow — would both otherwise be counted from the position the
           first one started from, and the second would go nowhere. */
        group.index = clamped;
        this.paint(group);

        const trackBox = group.track.getBoundingClientRect();
        const tileBox = tile.getBoundingClientRect();
        group.track.scrollTo({
          left: group.track.scrollLeft + (tileBox.left - trackBox.left),
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      queueSync(group) {
        if (group.queued) return;
        group.queued = true;
        requestAnimationFrame(() => {
          group.queued = false;
          this.sync(group);
        });
      }

      /* The bar is only of use while the strip has somewhere to scroll to —
         which is a question of how wide it is, not of which width class the
         page is in, so it is asked of the element itself. */
      sync(group) {
        if (!group.track || !group.nav) return;

        const room = group.track.scrollWidth - group.track.clientWidth;
        if (room <= 1) {
          group.nav.hidden = true;
          group.pages = 0;
          return;
        }

        /* Tile offsets within the scrolled content. Read off the boxes rather
           than offsetLeft, which is measured from whichever ancestor happens
           to be positioned and need not be the strip. */
        const edge = group.track.getBoundingClientRect().left;
        const at = group.track.scrollLeft;
        const offsets = group.tiles.map((tile) => tile.getBoundingClientRect().left - edge + at);

        /* One stop per place the strip can come to rest. A tile whose start
           lies past the end of the scroll is still reachable — it just arrives
           in the middle or at the end of the view, never at the start — so it
           is not a position of its own. Hence four tiles three-up is two. */
        const pages = offsets.filter((x) => x <= room + 1).length;

        group.nav.hidden = pages < 2;
        if (pages < 2) {
          group.pages = pages;
          return;
        }

        let index = 0;
        let closest = Infinity;
        for (let i = 0; i < pages; i++) {
          const distance = Math.abs(offsets[i] - at);
          if (distance < closest) {
            closest = distance;
            index = i;
          }
        }

        if (pages === group.pages && index === group.index) return;
        group.pages = pages;
        group.index = index;

        this.paint(group);
      }

      /* The bar drawn from what the group is currently taken to be showing. */
      paint(group) {
        const { index, pages } = group;
        if (pages < 2) return;

        if (group.prev) group.prev.disabled = index === 0;
        if (group.next) group.next.disabled = index === pages - 1;

        const of = group.dots?.dataset.of || 'of';
        group.nav.querySelectorAll('[data-goto]').forEach((dot, i) => {
          /* the spares stay in the markup — a resize can want them back */
          dot.parentElement.hidden = i >= pages;
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
          const label = dot.querySelector('span');
          if (label && i < pages) label.textContent = `${i + 1} ${of} ${pages}`;
        });
      }
    }
  );
}
